import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { login as loginApi } from "../api/authApi.js";

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: sessionStorage.getItem("user") || null,
    profile: sessionStorage.getItem("profile") || null,
    token: sessionStorage.getItem("token") || null,
    roles: JSON.parse(sessionStorage.getItem("roles") || "[]"),
    impersonator: JSON.parse(sessionStorage.getItem("impersonator") || "null"),
  },
  reducers: {
    setCredentials: (state, action) => {
      state.user = action.payload.user;
      state.profile = action.payload.profile;
      state.token = action.payload.token;
      state.roles = action.payload.roles;
      sessionStorage.setItem("token", action.payload.token);
      sessionStorage.setItem("roles", JSON.stringify(action.payload.roles));
      sessionStorage.setItem("profile", action.payload.profile);
      sessionStorage.setItem("user", action.payload.user);
    },
    logout: (state) => {
      state.user = null;
      state.profile = null;
      state.token = null;
      state.roles = [];
      state.impersonator = null;
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("roles");
      sessionStorage.removeItem("profile");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("impersonator");
    },
    startImpersonation: (state, action) => {
      if (!state.impersonator) {
        state.impersonator = {
          original: {
            user: state.user,
            profile: state.profile,
            token: state.token,
            roles: state.roles,
          },
          asUsername: action.payload.username,
        };
        sessionStorage.setItem(
          "impersonator",
          JSON.stringify(state.impersonator),
        );
      }
      state.user = action.payload.user;
      state.profile = action.payload.profile;
      state.token = action.payload.token;
      state.roles = action.payload.roles;
      sessionStorage.setItem("token", action.payload.token);
      sessionStorage.setItem("roles", JSON.stringify(action.payload.roles));
      sessionStorage.setItem("profile", action.payload.profile ?? "");
      sessionStorage.setItem("user", action.payload.user);
    },
    stopImpersonation: (state) => {
      const imp = state.impersonator;
      if (!imp) return;
      const o = imp.original;
      state.user = o.user;
      state.profile = o.profile;
      state.token = o.token;
      state.roles = o.roles;
      state.impersonator = null;
      sessionStorage.setItem("token", o.token);
      sessionStorage.setItem("roles", JSON.stringify(o.roles));
      sessionStorage.setItem("profile", o.profile ?? "");
      sessionStorage.setItem("user", o.user);
      sessionStorage.removeItem("impersonator");
    },
  },
  // Extra Reducers (Listening to the Login Thunk)
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.profile = action.payload.profile;
        state.token = action.payload.token;
        state.roles = action.payload.roles;

        sessionStorage.setItem("token", action.payload.token);
        sessionStorage.setItem("roles", JSON.stringify(action.payload.roles));
        sessionStorage.setItem("profile", action.payload.profile);
        sessionStorage.setItem("user", action.payload.user);
      })
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload; // This is the message from rejectWithValue
      });
  },
});

export const { setCredentials, logout, startImpersonation, stopImpersonation } =
  authSlice.actions;

export const login = createAsyncThunk(
  "auth/login",
  async ({ username, password }, { rejectWithValue }) => {
    try {
      return await loginApi(username, password);
    } catch (error) {
      if (!error.status) {
        return rejectWithValue("login.unexpected_error");
      }
      switch (error.status) {
        case 401:
          return rejectWithValue("login.invalid_credentials");
        case 403:
          return rejectWithValue("login.user_inactive");
        default:
          return rejectWithValue("login.temporal_failure");
      }
    }
  },
);

export default authSlice.reducer;
